/**
 * SiriHowTo - siri_08. How to log an expense with Siri.
 * Opened from Settings ("Log with Siri") and from the foot of Add Expense.
 * Follows the theme: pink, warm, blue, and the money plan look.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePlan } from '../../utils/plan'; // reorder_02

const PALETTES = {
  pink: {
    page: '#FBEFF5', font: "'DM Sans', sans-serif", title: "'DM Serif Display', serif", titleWeight: 400,
    say: ['#E9E1F5', '#7A5FB0'], steps: [['#FAE3EC', '#C23A72'], ['#DBE7F5', '#3C6693'], ['#E9E1F5', '#7A5FB0']],
    reply: ['#E7F1DB', '#5C7A4A'], tip: ['#FFF4CF', '#8C7424'], quick: ['#DBE7F5', '#3C6693'],
  },
  warm: {
    page: '#FAF7F2', font: "'DM Sans', sans-serif", title: "'DM Serif Display', serif", titleWeight: 400,
    say: ['#E1EBDD', '#2D5A3D'], steps: [['#F6E3E0', '#A04A3C'], ['#DFE8F3', '#3C6693'], ['#E1EBDD', '#2D5A3D']],
    reply: ['#E1EBDD', '#2D5A3D'], tip: ['#FFF4CF', '#8C7424'], quick: ['#DFE8F3', '#3C6693'],
  },
  blue: {
    page: '#EEF2FF', font: "'DM Sans', sans-serif", title: "'DM Serif Display', serif", titleWeight: 400,
    say: ['#DCE3F8', '#3A4DA8'], steps: [['#E9E1F5', '#6B4FB0'], ['#DCE3F8', '#3A4DA8'], ['#DDEFE6', '#2E7A55']],
    reply: ['#DDEFE6', '#2E7A55'], tip: ['#FFF4CF', '#8C7424'], quick: ['#DCE3F8', '#3A4DA8'],
  },
  plan: {
    page: '#F3F0FA', font: 'inherit', title: 'inherit', titleWeight: 600,
    say: ['#EAE5F7', '#7B5FD6'], steps: [['#FAE3EC', '#C2477E'], ['#DDE8F8', '#3767B5'], ['#EAE5F7', '#7B5FD6']],
    reply: ['#DEF1E7', '#2E8A55'], tip: ['#FFF4CF', '#8C7424'], quick: ['#DDE8F8', '#3767B5'],
  },
};

export const siriPalette = (theme, plan) => PALETTES[plan ? 'plan' : theme] || PALETTES.pink;

// The little row that opens this page. Used at the foot of Add Expense.
export function SiriLink({ theme, plan, onClick }) {
  const P = siriPalette(theme, plan);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="siri-howto-link"
      className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 mt-3 text-left"
      style={{ background: P.say[0], fontFamily: P.font }}
    >
      <span className="text-lg">🎙️</span>
      <span className="flex-1">
        <span className="block text-[14px] font-semibold text-gray-800">Log with Siri</span>
        <span className="block text-[12.5px]" style={{ color: P.say[1] }}>Add expenses hands-free next time</span>
      </span>
      <span className="text-lg" style={{ color: P.say[1] }}>›</span>
    </button>
  );
}

export default function SiriHowTo({ open, onClose, theme = 'pink', plan = false }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  if (!open) return null;
  const P = siriPalette(theme, plan);
  const steps = [
    ['How much was it?', '$6.50'],
    ['Which category?', 'Eating out'],
    ['Which card did you use?', 'Visa'],
  ];
  const box = (bg, extra = {}) => ({ background: bg, borderRadius: 20, padding: '13px 15px', marginTop: 12, fontSize: 14, lineHeight: 1.45, ...extra });
  return createPortal(
    <div className="fixed inset-0 z-[400] flex items-end" data-testid="siri-howto" style={{ fontFamily: P.font, pointerEvents: 'auto' }}>{/* reorder_03 */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full bg-white rounded-t-[28px] px-5 pt-3 overflow-y-auto overscroll-contain" onClick={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()} onWheel={(e) => e.stopPropagation()} style={{ maxHeight: '92vh', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 22px)', WebkitOverflowScrolling: 'touch' }}>
        <div className="w-10 h-1.5 rounded-full bg-gray-200 mx-auto mb-4" />
        <h2 style={{ fontFamily: P.title, fontWeight: P.titleWeight, fontSize: 28, lineHeight: 1.1, color: '#2B2730', margin: 0 }}>Log with Siri 🎙️</h2>
        <p className="text-[14.5px] text-gray-500 mt-1.5 leading-snug">Add an expense in a few seconds, without even opening Worthly!</p>

        <div style={{ background: P.say[0], borderRadius: 24, padding: '18px 18px 20px', marginTop: 18 }}>
          <span className="block text-[12.5px] font-semibold" style={{ color: P.say[1] }}>Just say</span>
          <span className="block text-[20px] font-semibold leading-snug mt-1.5 text-gray-800">"Hey Siri, log an expense in Worthly"</span>
          <span className="block text-[13px] mt-2" style={{ color: P.say[1] }}>"Add an expense in Worthly" works too!</span>
        </div>

        <div className="mt-4">
          {steps.map(([q, a], i) => (
            <div key={q} className="flex items-center gap-3 py-2.5" style={i ? { borderTop: '1px solid #F3EDF1' } : null}>
              <span className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-[13px] font-semibold flex-none" style={{ background: P.steps[i][0], color: P.steps[i][1] }}>{i + 1}</span>
              <span className="flex-1 text-[14.5px] text-gray-800">{q}</span>
              <span className="text-[13px] font-semibold rounded-full px-3 py-1.5 flex-none" style={{ background: P.steps[i][0], color: P.steps[i][1] }}>{a}</span>
            </div>
          ))}
        </div>

        <div style={box(P.reply[0], { color: P.reply[1] })}><b className="font-semibold">Siri:</b> Added $6.50 to Eating out! You have $84.00 left in Eating out this month.</div>
        <div style={box(P.tip[0], { color: '#5E5132' })}><b className="font-semibold" style={{ color: P.tip[1] }}>Emojis in your categories?</b> Just say the words. For 🍔 Eating out, say "Eating out" and Siri will find it.</div>
        <div style={box(P.quick[0], { color: '#34506F' })}><b className="font-semibold" style={{ color: P.quick[1] }}>Want it even quicker?</b> Find "Log an expense" under Worthly in the Shortcuts app, then add it to your Home Screen or your Action Button.</div>

        <button type="button" onClick={onClose} className="w-full rounded-full py-3.5 mt-4 text-white text-[15px] font-semibold" style={{ background: '#1F1D24' }}>Got it</button>
        <p className="text-[12px] text-gray-400 text-center mt-2.5">Expenses go into this month's budget. You need to be signed in to Worthly on this phone.</p>
      </div>
    </div>,
    document.body
  );
}

// reorder_02: the page can be opened from anywhere with openSiriHelp().
// SiriHowToHost sits in the app itself (not inside Settings), so it still
// shows after Settings closes. Opened from Settings, Got it goes back there.
export const openSiriHelp = (from = 'app') => {
  window.dispatchEvent(new CustomEvent('worthly_open_siri_help', { detail: { from } }));
};

export function SiriHowToHost({ theme = 'pink', onBackToSettings }) {
  const plan = usePlan();
  const [from, setFrom] = useState(null);
  useEffect(() => {
    const onOpen = (e) => setFrom((e && e.detail && e.detail.from) || 'app');
    window.addEventListener('worthly_open_siri_help', onOpen);
    return () => window.removeEventListener('worthly_open_siri_help', onOpen);
  }, []);
  const close = () => {
    const was = from;
    setFrom(null);
    if (was === 'settings' && onBackToSettings) setTimeout(onBackToSettings, 250);
  };
  return <SiriHowTo open={!!from} onClose={close} theme={theme} plan={plan} />;
}
